import { useState, useEffect } from 'react';
import {
  Title,
  useDataProvider,
  useNotify,
  useRecordContext,
} from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  FormControlLabel,
  FormHelperText,
} from '@mui/material';
import Papa from 'papaparse';
import { apiFetch } from '../providers/api-rest-provider';
import {
  detectVendor,
  getDefaultColumnMapping,
  mapRowToRecord,
} from './wearables-helpers';
import type {
  BiometricContext,
  Student,
} from '../types';

// ---------------------------------------------------------------------------
// PhysiologicalContextPanel
// ---------------------------------------------------------------------------

function fmtSleep(min: number | null | undefined): string {
  if (min == null) return '—';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h ${m}m`;
}

export const PhysiologicalContextPanel = ({
  biometricContext,
}: {
  biometricContext: BiometricContext | null | undefined;
}) => {
  if (!biometricContext) return null;

  const { readiness, effort, strain_trend, attached_at } = biometricContext;

  const sourceLabel =
    readiness?.source ?? effort?.source ?? 'unknown';

  // Format attached_at date
  let importedDate = attached_at;
  try {
    importedDate = new Date(attached_at).toISOString().slice(0, 10);
  } catch {
    // keep raw
  }

  return (
    <Box mt={2} p={2} border={1} borderColor="divider" borderRadius={1}>
      <Typography variant="h6" gutterBottom>
        Physiological Context
      </Typography>

      {effort && (
        <Box mb={2}>
          <Typography variant="subtitle2">Effort</Typography>
          <Typography variant="body2">
            Avg HR: {effort.avg_hr ?? '—'} bpm &nbsp;|&nbsp; Max HR: {effort.max_hr ?? '—'} bpm
          </Typography>
          {effort.hr_zones && Object.keys(effort.hr_zones).length > 0 && (
            <Typography variant="body2">
              HR Zones:{' '}
              {Object.entries(effort.hr_zones)
                .map(([zone, val]) => `${zone}: ${val}`)
                .join(', ')}
            </Typography>
          )}
        </Box>
      )}

      {readiness && (
        <Box mb={2}>
          <Typography variant="subtitle2">Readiness</Typography>
          <Typography variant="body2">
            HRV: {readiness.hrv_ms != null ? `${readiness.hrv_ms} ms` : '—'}
          </Typography>
          <Typography variant="body2">
            Resting HR: {readiness.resting_hr != null ? `${readiness.resting_hr} bpm` : '—'}
          </Typography>
          <Typography variant="body2">
            Sleep: {fmtSleep(readiness.sleep_duration_min)}
            {readiness.sleep_score != null ? ` (score: ${readiness.sleep_score})` : ''}
          </Typography>
          <Typography variant="body2">
            Readiness Score: {readiness.readiness_score ?? '—'}
          </Typography>
        </Box>
      )}

      {strain_trend && (
        <Box mb={2}>
          <Typography variant="subtitle2">Strain Trend</Typography>
          <Typography variant="body2">
            7d avg: {strain_trend.last7_avg ?? '—'} &nbsp;|&nbsp; 28d avg:{' '}
            {strain_trend.last28_avg ?? '—'}
          </Typography>
        </Box>
      )}

      <Typography variant="caption" color="text.secondary">
        {sourceLabel} · imported {importedDate}
      </Typography>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// WearableSettingsPage
// ---------------------------------------------------------------------------

export const WearableSettingsPage = () => {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const notify = useNotify();

  useEffect(() => {
    apiFetch('tenants/self')
      .then((s: any) => {
        setEnabled(s?.settings?.wearables?.enabled === true);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleToggle = async (newValue: boolean) => {
    setSaving(true);
    try {
      await apiFetch('tenants/self', {
        method: 'PUT',
        body: JSON.stringify({ settings: { wearables: { enabled: newValue } } }),
      });
      setEnabled(newValue);
      notify(newValue ? 'Wearable data enabled' : 'Wearable data disabled', {
        type: 'success',
      });
    } catch {
      notify('Failed to save setting', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box p={3}>
      <Title title="Wearable Settings" />
      <Typography variant="h5" gutterBottom>
        Wearable Settings
      </Typography>
      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => handleToggle(e.target.checked)}
                disabled={saving}
              />
            }
            label="Wearable Data"
          />
          <FormHelperText>
            When enabled, physiological context from wearable devices can be
            attached to student attempts. Students must grant consent before
            any data is shown.
          </FormHelperText>
        </Box>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// ConsentDashboardPage
// ---------------------------------------------------------------------------

interface ConsentRow {
  student_id: string;
  student_name: string;
  status: 'granted' | 'revoked' | 'none';
  basis?: string;
}

export const ConsentDashboardPage = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [rows, setRows] = useState<ConsentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogStudentId, setDialogStudentId] = useState('');
  const [dialogAction, setDialogAction] = useState<'grant' | 'revoke'>('grant');
  const [basisText, setBasisText] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      dataProvider.getList('students', {
        sort: { field: 'last_name', order: 'ASC' },
        pagination: { page: 1, perPage: 1000 },
        filter: {},
      }),
      apiFetch('wearable-consents').catch(() => ({ items: [] })),
    ])
      .then(([{ data: students }, consentsRaw]: [any, any]) => {
        const consents: Record<string, any> = {};
        const items = Array.isArray(consentsRaw)
          ? consentsRaw
          : consentsRaw?.items ?? [];
        for (const c of items) {
          consents[c.student_id] = c;
        }
        setRows(
          students.map((s: any) => ({
            student_id: s.id,
            student_name: `${s.first_name} ${s.last_name}`.trim(),
            status: consents[s.id]?.status ?? 'none',
            basis: consents[s.id]?.basis,
          })),
        );
      })
      .catch(() => notify('Failed to load consent data', { type: 'error' }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDialog = (studentId: string, action: 'grant' | 'revoke') => {
    setDialogStudentId(studentId);
    setDialogAction(action);
    setBasisText('');
    setDialogOpen(true);
  };

  const handleConfirm = async () => {
    if (dialogAction === 'grant' && !basisText.trim()) return;
    setSaving(true);
    try {
      await apiFetch(`wearable-consents/${dialogStudentId}`, {
        method: 'PUT',
        body: JSON.stringify({
          status: dialogAction === 'grant' ? 'granted' : 'revoked',
          basis: basisText.trim() || undefined,
        }),
      });
      notify(
        dialogAction === 'grant' ? 'Consent granted' : 'Consent revoked',
        { type: 'success' },
      );
      setDialogOpen(false);
      load();
    } catch {
      notify('Failed to update consent', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const statusChip = (status: string) => {
    if (status === 'granted')
      return <Chip label="Granted" color="success" size="small" />;
    if (status === 'revoked')
      return <Chip label="Revoked" color="error" size="small" />;
    return <Chip label="None" size="small" />;
  };

  return (
    <Box p={3}>
      <Title title="Wearable Consent Dashboard" />
      <Typography variant="h5" gutterBottom>
        Wearable Consent Dashboard
      </Typography>
      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Student</TableCell>
                <TableCell>Consent Status</TableCell>
                <TableCell>Basis</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.student_id}>
                  <TableCell>{row.student_name}</TableCell>
                  <TableCell>{statusChip(row.status)}</TableCell>
                  <TableCell>{row.basis ?? '—'}</TableCell>
                  <TableCell>
                    {row.status !== 'granted' && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        onClick={() => openDialog(row.student_id, 'grant')}
                        sx={{ mr: 1 }}
                      >
                        Grant
                      </Button>
                    )}
                    {row.status === 'granted' && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => openDialog(row.student_id, 'revoke')}
                      >
                        Revoke
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogAction === 'grant' ? 'Grant Consent' : 'Revoke Consent'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This is the student's own physiological data. Consent can be revoked
            at any time and data will be hidden from all views immediately.
          </Typography>
          <TextField
            fullWidth
            label={dialogAction === 'grant' ? 'Basis (required)' : 'Basis (optional)'}
            value={basisText}
            onChange={(e) => setBasisText(e.target.value)}
            multiline
            rows={2}
            helperText={
              dialogAction === 'grant'
                ? 'Describe the legal or policy basis for consent.'
                : 'Optional reason for revocation.'
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={saving || (dialogAction === 'grant' && !basisText.trim())}
            variant="contained"
            color={dialogAction === 'grant' ? 'success' : 'error'}
          >
            {saving ? <CircularProgress size={18} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// BiometricImportPage
// ---------------------------------------------------------------------------

const CHAMBER_FIELDS = [
  'date',
  'hrv_ms',
  'resting_hr',
  'sleep_duration_min',
  'sleep_score',
  'readiness_score',
  'strain',
];

export const BiometricImportPage = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [step, setStep] = useState(1);
  const [students, setStudents] = useState<any[]>([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [consentStatus, setConsentStatus] = useState<string | null>(null);
  const [consentLoading, setConsentLoading] = useState(false);

  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvRows, setCsvRows] = useState<Record<string, string>[]>([]);
  const [vendor, setVendor] = useState<'oura' | 'whoop' | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});

  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState<any>(null);

  // Load students on mount
  useEffect(() => {
    dataProvider
      .getList('students', {
        sort: { field: 'last_name', order: 'ASC' },
        pagination: { page: 1, perPage: 1000 },
        filter: {},
      })
      .then(({ data }) => setStudents(data))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStudentSelect = (studentId: string) => {
    setSelectedStudentId(studentId);
    setConsentStatus(null);
    if (!studentId) return;
    setConsentLoading(true);
    apiFetch(`wearable-consents/${studentId}`)
      .then((c: any) => setConsentStatus(c?.status ?? 'none'))
      .catch(() => setConsentStatus('none'))
      .finally(() => setConsentLoading(false));
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const headers = result.meta.fields ?? [];
        const rows = result.data as Record<string, string>[];
        setCsvHeaders(headers);
        setCsvRows(rows);
        const detectedVendor = detectVendor(headers);
        setVendor(detectedVendor);
        setMapping(getDefaultColumnMapping(detectedVendor));
        setStep(3);
      },
    });
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const records = csvRows
        .map((row) => mapRowToRecord(row, mapping))
        .filter(Boolean);
      const report = await apiFetch('biometrics/import', {
        method: 'POST',
        body: JSON.stringify({ student_id: selectedStudentId, records }),
      });
      setImportReport(report);
      setStep(4);
      notify('Import complete', { type: 'success' });
    } catch {
      notify('Import failed', { type: 'error' });
    } finally {
      setImporting(false);
    }
  };

  return (
    <Box p={3}>
      <Title title="Import Biometric Data" />
      <Typography variant="h5" gutterBottom>
        Import Biometric Data
      </Typography>

      {/* Step 1: Student picker */}
      <Box mb={3}>
        <Typography variant="subtitle1" gutterBottom>
          Step 1: Select Student
        </Typography>
        <Select
          value={selectedStudentId}
          onChange={(e) => handleStudentSelect(e.target.value as string)}
          displayEmpty
          sx={{ minWidth: 300, mr: 2 }}
        >
          <MenuItem value="">
            <em>Select a student…</em>
          </MenuItem>
          {students.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.first_name} {s.last_name}
            </MenuItem>
          ))}
        </Select>
        {consentLoading && <CircularProgress size={20} />}
        {consentStatus === 'granted' && (
          <Chip label="Consent granted" color="success" size="small" />
        )}
        {consentStatus === 'revoked' && (
          <Chip label="Consent revoked" color="error" size="small" />
        )}
        {consentStatus === 'none' && (
          <Chip label="No consent on file" size="small" />
        )}
        {consentStatus && consentStatus !== 'granted' && (
          <Alert severity="error" sx={{ mt: 1 }}>
            This student has not granted consent. Data cannot be imported.
          </Alert>
        )}
      </Box>

      {/* Step 2: File upload */}
      {consentStatus === 'granted' && (
        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom>
            Step 2: Upload CSV File
          </Typography>
          <Button variant="outlined" component="label">
            Choose File
            <input
              type="file"
              accept=".csv,.json"
              hidden
              onChange={handleFileUpload}
            />
          </Button>
          {vendor && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Detected vendor: <strong>{vendor}</strong>
            </Typography>
          )}
          {csvHeaders.length > 0 && !vendor && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Vendor not detected — please configure column mapping manually.
            </Typography>
          )}
        </Box>
      )}

      {/* Step 3: Column mapping */}
      {step >= 3 && csvHeaders.length > 0 && consentStatus === 'granted' && (
        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom>
            Step 3: Column Mapping
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>CHAMBER Field</TableCell>
                  <TableCell>CSV Column</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {CHAMBER_FIELDS.map((field) => (
                  <TableRow key={field}>
                    <TableCell>{field}</TableCell>
                    <TableCell>
                      <Select
                        value={mapping[field] ?? ''}
                        onChange={(e) =>
                          setMapping((prev) => ({
                            ...prev,
                            [field]: e.target.value as string,
                          }))
                        }
                        displayEmpty
                        size="small"
                        sx={{ minWidth: 200 }}
                      >
                        <MenuItem value="">
                          <em>(none)</em>
                        </MenuItem>
                        {csvHeaders.map((h) => (
                          <MenuItem key={h} value={h}>
                            {h}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Preview first 5 rows */}
          <Typography variant="subtitle2" gutterBottom>
            Preview (first 5 rows)
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 2, overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {csvHeaders.map((h) => (
                    <TableCell key={h}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {csvRows.slice(0, 5).map((row, i) => (
                  <TableRow key={i}>
                    {csvHeaders.map((h) => (
                      <TableCell key={h}>{row[h] ?? ''}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Button
            variant="contained"
            onClick={handleImport}
            disabled={importing || !selectedStudentId}
          >
            {importing ? <CircularProgress size={18} /> : 'Import'}
          </Button>
        </Box>
      )}

      {/* Step 4: Report */}
      {step === 4 && importReport && (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Import Report
          </Typography>
          <pre style={{ background: '#f5f5f5', padding: 16, borderRadius: 4 }}>
            {JSON.stringify(importReport, null, 2)}
          </pre>
          <Button
            variant="outlined"
            onClick={() => {
              setStep(1);
              setSelectedStudentId('');
              setConsentStatus(null);
              setCsvHeaders([]);
              setCsvRows([]);
              setVendor(null);
              setMapping({});
              setImportReport(null);
            }}
            sx={{ mt: 1 }}
          >
            Import Another
          </Button>
        </Box>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// BiometricsTab (used in StudentShow)
// ---------------------------------------------------------------------------

export const BiometricsTab = () => {
  const record = useRecordContext<Student>();
  const [consent, setConsent] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!record?.id) return;
    setLoading(true);
    apiFetch(`wearable-consents/${record.id}`)
      .then((c: any) => setConsent(c))
      .catch(() => {})
      .finally(() => setLoading(false));
    apiFetch(`biometrics/student/${record.id}`)
      .then((data: any) => setItems(data?.items ?? []))
      .catch(() => {});
  }, [record?.id]);

  if (loading) return <CircularProgress />;

  const status = consent?.status ?? 'none';

  return (
    <Box p={2}>
      <Box mb={2}>
        <Typography variant="subtitle2" gutterBottom>
          Consent Status
        </Typography>
        {status === 'granted' && <Chip label="Granted" color="success" size="small" />}
        {status === 'revoked' && <Chip label="Revoked" color="error" size="small" />}
        {status === 'none' && <Chip label="None" size="small" />}
        {consent?.basis && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            Basis: {consent.basis}
          </Typography>
        )}
      </Box>
      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No biometric records imported.
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Kind</TableCell>
                <TableCell>HRV (ms)</TableCell>
                <TableCell>Resting HR</TableCell>
                <TableCell>Sleep</TableCell>
                <TableCell>Readiness</TableCell>
                <TableCell>Strain</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item: any, i: number) => (
                <TableRow key={i}>
                  <TableCell>{item.date ?? '—'}</TableCell>
                  <TableCell>{item.kind ?? '—'}</TableCell>
                  <TableCell>{item.hrv_ms ?? '—'}</TableCell>
                  <TableCell>{item.resting_hr ?? '—'}</TableCell>
                  <TableCell>{item.sleep_duration_min != null ? `${item.sleep_duration_min} min` : '—'}</TableCell>
                  <TableCell>{item.readiness_score ?? '—'}</TableCell>
                  <TableCell>{item.strain ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};
